/**
 * SE-5492 — lazy card mounting for long kanban columns.
 *
 * @remarks
 * ## Why lazy mounting, not windowing
 *
 * The obvious "virtualization" is a window that UNMOUNTS off-screen rows. That
 * is the wrong tool for THIS board, and the reason is dnd-kit:
 *
 * - Cross-column drag registers each card as a `useDraggable` node
 *   (`KanbanPlayerCardView`), and rank-reorder registers each as a sortable
 *   inside a `SortableContext` (`KanbanColumnContentView`). A row that unmounts
 *   while a drag is in flight tears its node out of the dnd-kit registry
 *   mid-gesture — the classic virtualized-board failure.
 * - Rows are also not fixed-height: tag count, the "show more" toggle, the
 *   pipeline-only days-in-stage row and the stale badge all change row height,
 *   so a window would need measurement to avoid scroll-anchor jumps.
 *
 * This hook instead mounts an initial chunk and grows monotonically — it NEVER
 * unmounts a row. That makes the drag failure mode structurally impossible
 * rather than merely unlikely, and it is what the acceptance criterion actually
 * asks for: "virtualized/**lazy-loaded**" (GD-315).
 *
 * ## The honest trade-off
 *
 * Growth-only means a user who scrolls a 100-card column to the bottom ends up
 * with all 100 mounted, so this buys **time-to-interactive** and **scroll
 * smoothness**, NOT a steady-state DOM/memory ceiling. A true windowing pass
 * would buy the ceiling too, at the cost of the drag-safety property above; it
 * needs its own ticket and a measured drag-regression suite.
 *
 * ## Mechanism
 *
 * A sentinel element is rendered after the last mounted card. When it comes
 * within `ROOT_MARGIN` of the viewport, the next chunk mounts. Because the
 * board's vertical scroll lives on an ancestor `ScrollView` (and the content is
 * `transform: scale(0.8)` on web), a scroll-offset calculation would have to
 * know about both; `IntersectionObserver` with `root: null` reports real
 * rendered geometry against the viewport and is correct through ancestor
 * clipping and transforms alike.
 *
 * The growth step itself is dispatched inside `startTransition` (SE-6548). A
 * chunk mount is the hook's one expensive commit, and as an urgent update it is
 * a single uninterruptible render — QA measured a 575ms longest task under 4x
 * CPU throttling against a 50ms long-task bar. As a transition React 19 yields
 * between card subtrees, slicing that render instead of blocking the main
 * thread. The growth-only contract is untouched: a transition changes WHEN the
 * work is done, never whether a row mounts or unmounts.
 *
 * Measured on a 123-card board (Chromium, local dev-pointed export): 15 cards
 * mounted at rest instead of 123; a full-column scroll that mounts the
 * remaining 108 costs 4 frames over 33ms, and a second scroll pass with
 * everything already mounted drops zero frames — i.e. the residual jank is
 * mount cost, not scroll cost.
 * @module features/player-kanban/hooks/useLazyColumnCards
 */
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

/**
 * Cards mounted on first paint, per column.
 * @remarks
 * A 300px-tall viewport region shows ~4 cards; 12 covers the tallest common
 * column viewport plus headroom, so the first paint is never visibly short.
 * With the board's default 40-card page this caps initial card mounts at 12
 * per column instead of 40.
 */
export const INITIAL_CHUNK = 12;

/** Cards added each time the sentinel comes into range. */
export const CHUNK_STEP = 12;

/**
 * How far below the viewport the sentinel triggers.
 * @remarks
 * Deliberately ~2 chunks of scroll: the next chunk mounts before the user can
 * reach blank space, which is what keeps the scroll free of mount-induced
 * frame drops rather than merely moving them later.
 */
export const ROOT_MARGIN = "600px";

/**
 * Advance the release counter by one chunk.
 * @remarks
 * A functional updater, because the committed value is the only safe base —
 * several observer entries can arrive in a single tick. Declared at module
 * scope rather than inline so the growth dispatch stays within the repo's
 * function-nesting limit.
 * @param previous - The currently committed release count.
 * @returns The count with one chunk added.
 */
const growByOneChunk = (previous: number): number => previous + CHUNK_STEP;

/** What {@link useLazyColumnCards} returns. */
export interface LazyColumnCards {
  /** How many of the column's cards should currently be rendered. */
  readonly visibleCount: number;
  /** Whether a sentinel should be rendered (i.e. cards remain unmounted). */
  readonly hasMore: boolean;
  /** Ref to attach to the sentinel element. */
  readonly sentinelRef: (node: unknown) => void;
}

/** Inputs to {@link useLazyColumnCards}. */
export interface LazyColumnCardsInput {
  /** Total cards in the column. */
  readonly total: number;
  /**
   * Whether a drag is active anywhere on the board. Growth pauses during a
   * drag so a mount batch cannot land mid-gesture and cost frames while
   * dnd-kit is tracking pointer movement. Nothing unmounts either way.
   */
  readonly isDragging?: boolean;
}

/**
 * Whether lazy mounting can run in this environment.
 * @remarks
 * Capability gate, NOT a device-type gate: `IntersectionObserver` is a DOM API.
 * On native (and any no-DOM environment such as jest without a DOM shim) the
 * hook reports every card visible, so behaviour is unchanged there rather than
 * silently truncated — a column must never hide cards it cannot reveal.
 * @returns `true` when the observer-driven path is available.
 */
const canObserve = (): boolean =>
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  typeof IntersectionObserver !== "undefined";

/**
 * Progressively mount a column's cards as the user scrolls toward them.
 * @param input - {@link LazyColumnCardsInput}
 * @param input.total - Total cards in the column.
 * @param input.isDragging - Whether a board drag is active; pauses growth.
 * @returns The visible-count state and the sentinel ref to attach.
 */
export const useLazyColumnCards = ({
  total,
  isDragging = false,
}: LazyColumnCardsInput): LazyColumnCards => {
  const observable = canObserve();

  /**
   * Monotonic growth counter — how many cards lazy mounting has released so
   * far, independent of how many the column currently holds.
   */
  const [released, setReleased] = useState(INITIAL_CHUNK);

  const [sentinel, setSentinel] = useState<Element | null>(null);

  /**
   * Re-entry guard. The observer callback can fire more than once before React
   * commits the resulting state, and both firings would otherwise schedule a
   * growth step. State commits asynchronously, so a state-only guard reads
   * stale — this repo has shipped that bug twice (SE-5494 `handleSelectStage`,
   * SE-5504 `loadMore`). The ref flips synchronously and is cleared by the
   * effect below once the committed count has caught up.
   */
  const isGrowingRef = useRef(false);

  const sentinelRef = useCallback((node: unknown) => {
    // The sentinel is a react-native-web view, so the ref is an unknown-typed
    // host node. Only a real DOM Element can be observed.
    setSentinel(
      typeof Element !== "undefined" && node instanceof Element ? node : null
    );
  }, []);

  // Derived at RENDER time, never via a reset effect: clamping in an effect
  // would be exactly the `react-hooks/set-state-in-effect` pattern this repo
  // bans. A column whose card count shrinks (card dragged out, board refetch)
  // therefore clamps down for free, with no extra render.
  const visibleCount = observable ? Math.min(released, total) : total;
  const hasMore = visibleCount < total;

  // The guard is released by `released` COMMITTING, never by the observer
  // effect re-running (SE-6548). Those were the same moment while growth was an
  // urgent update, and are not once it is a transition: a pending transition
  // can be outlived by a dependency change (`sentinel`, `isDragging`,
  // `observable`, `hasMore`, `visibleCount`), and clearing the guard on that
  // re-run would let the still-intersecting sentinel schedule a SECOND
  // `+CHUNK_STEP` for growth that has not landed yet — a 24-card mount step,
  // the opposite of what this ticket is for. Keying the reset on `released`
  // ties it to the only event that actually means "the previous batch is in".
  useEffect(() => {
    isGrowingRef.current = false;
  }, [released]);

  useEffect(() => {
    if (!observable || !sentinel || isDragging || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        const isVisible = entries.some(entry => entry.isIntersecting);
        if (!isVisible || isGrowingRef.current) return;

        isGrowingRef.current = true;
        // SE-6548 — the growth step is a TRANSITION, not an urgent update.
        //
        // A chunk mount is the single most expensive thing this hook causes:
        // CHUNK_STEP cards, each with its own subtree, rendered in one commit.
        // As an urgent update that render is ONE uninterruptible task, which is
        // exactly what QA measured under 4x CPU throttling (575ms longest task
        // vs the 50ms long-task bar). Marking it a transition lets React 19's
        // concurrent renderer yield to the browser between card subtrees, so
        // the same total work is spread across short slices and scrolling and
        // input stay responsive while the chunk renders.
        //
        // Nothing about the growth-only contract changes: the update is still
        // monotonic, still guarded against double-advance, and still never
        // unmounts a row — a transition only reorders WHEN React does the work,
        // never whether it happens.
        startTransition(() => setReleased(growByOneChunk));
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, isDragging, observable, hasMore, visibleCount]);

  return { visibleCount, hasMore, sentinelRef };
};
